import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Preloader({ onLoadingComplete }) {
  const [progress, setProgress] = useState(0);
  const [isOpening, setIsOpening] = useState(false);

  useEffect(() => {
    const TOTAL_DURATION = 3000; // 3 seconds total boot time
    const INTERVAL = 30; // updates every 30ms
    const step = 100 / (TOTAL_DURATION / INTERVAL);

    const timer = setInterval(() => {
      setProgress((prev) => {
        const next = prev + step;
        if (next >= 100) {
          clearInterval(timer);
          // Trigger door opening split animation
          setIsOpening(true);
          setTimeout(() => {
            if (onLoadingComplete) onLoadingComplete();
          }, 1100);
          return 100;
        }
        return Math.floor(next);
      });
    }, INTERVAL);

    return () => clearInterval(timer);
  }, [onLoadingComplete]);

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden pointer-events-auto select-none">
      {/* ======================================================== */}
      {/* 1. LEFT SHUTTER DOOR (Slides to Left upon opening)      */}
      {/* ======================================================== */}
      <motion.div
        initial={{ x: 0 }}
        animate={isOpening ? { x: '-100%' } : { x: 0 }}
        transition={{ duration: 1.1, ease: [0.77, 0, 0.175, 1] }}
        className="absolute top-0 left-0 w-1/2 h-full bg-[#1b2d48] border-r border-[#4779c4]/50 shadow-[10px_0_40px_rgba(71,121,196,0.3)] z-20 flex items-center justify-end overflow-hidden"
      >
        {/* Subtle background tech grid on left door */}
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `linear-gradient(to right, #4779c4 1px, transparent 1px), linear-gradient(to bottom, #4779c4 1px, transparent 1px)`,
            backgroundSize: '36px 36px'
          }}
        />
        {/* Decorative Sci-Fi Door Seam Lines & Chevron Accents */}
        <div className="absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-[#4779c4]/15 to-transparent flex flex-col justify-between py-12 items-end pr-3">
          <div className="text-[10px] font-mono text-[#4779c4] tracking-widest rotate-90 origin-right font-bold">
            SEC_GATE // L-01
          </div>
          <div className="flex flex-col gap-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="w-3 h-1 bg-[#4779c4]/50 rounded-full" />
            ))}
          </div>
          <div className="text-[9px] font-mono text-[#38bdf8]/60">SYS.ONLINE</div>
        </div>
      </motion.div>

      {/* ======================================================== */}
      {/* 2. RIGHT SHUTTER DOOR (Slides to Right upon opening)    */}
      {/* ======================================================== */}
      <motion.div
        initial={{ x: 0 }}
        animate={isOpening ? { x: '100%' } : { x: 0 }}
        transition={{ duration: 1.1, ease: [0.77, 0, 0.175, 1] }}
        className="absolute top-0 right-0 w-1/2 h-full bg-[#1b2d48] border-l border-[#4779c4]/50 shadow-[-10px_0_40px_rgba(71,121,196,0.3)] z-20 flex items-center justify-start overflow-hidden"
      >
        {/* Subtle background tech grid on right door */}
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `linear-gradient(to right, #4779c4 1px, transparent 1px), linear-gradient(to bottom, #4779c4 1px, transparent 1px)`,
            backgroundSize: '36px 36px'
          }}
        />
        {/* Decorative Sci-Fi Door Seam Lines & Chevron Accents */}
        <div className="absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-[#4779c4]/15 to-transparent flex flex-col justify-between py-12 items-start pl-3">
          <div className="text-[10px] font-mono text-[#4779c4] tracking-widest -rotate-90 origin-left font-bold">
            SEC_GATE // R-02
          </div>
          <div className="flex flex-col gap-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="w-3 h-1 bg-[#4779c4]/50 rounded-full" />
            ))}
          </div>
          <div className="text-[9px] font-mono text-[#38bdf8]/60">INIT.PROT</div>
        </div>
      </motion.div>

      {/* Center Laser Seam Split Glow Line (Visible until door opens) */}
      <motion.div
        animate={isOpening ? { opacity: 0, scaleY: 0 } : { opacity: [0.6, 1, 0.6] }}
        transition={isOpening ? { duration: 0.3 } : { duration: 1.5, repeat: Infinity }}
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[3px] h-full bg-[#00f0ff] shadow-[0_0_20px_#00f0ff,0_0_40px_#00f0ff] z-30"
      />

      {/* ======================================================== */}
      {/* 3. CENTER CYBER HUD ROTATING ARC REACTOR (The Image)     */}
      {/* ======================================================== */}
      <motion.div
        animate={isOpening ? { scale: 1.3, opacity: 0 } : { scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="absolute inset-0 flex flex-col items-center justify-center z-40 pointer-events-none"
      >
        <div className="relative w-[340px] h-[340px] sm:w-[460px] sm:h-[460px] flex items-center justify-center">
          
          {/* Outer Pulsing Aqua Glow Bloom */}
          <div className="absolute inset-0 bg-[#00f0ff]/15 rounded-full blur-[80px] animate-pulse" />

          {/* SVG HUD Arc Reactor Rings */}
          <svg
            className="w-full h-full"
            viewBox="0 0 500 500"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <filter id="hud-glow" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Outermost Segmented Dial Ring */}
            <circle
              cx="250"
              cy="250"
              r="230"
              fill="none"
              stroke="#00f0ff"
              strokeWidth="1.2"
              strokeDasharray="4 8"
              opacity="0.35"
            />

            {/* Ring 1 - Fast Clockwise Rotating Notched Tech Gear */}
            <motion.g
              animate={{ rotate: 360 }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              style={{ originX: "250px", originY: "250px" }}
            >
              <circle
                cx="250"
                cy="250"
                r="215"
                fill="none"
                stroke="#0284c7"
                strokeWidth="2.5"
                strokeDasharray="16 12 36 8 80 18"
                opacity="0.6"
              />
              <circle
                cx="250"
                cy="250"
                r="200"
                fill="none"
                stroke="#00f0ff"
                strokeWidth="6"
                strokeDasharray="40 50 80 40"
                opacity="0.45"
                filter="url(#hud-glow)"
              />
            </motion.g>

            {/* Ring 2 - Counter-Clockwise Segmented Thick Reactor Blades (As in Image) */}
            <motion.g
              animate={{ rotate: -360 }}
              transition={{ duration: 16, repeat: Infinity, ease: "linear" }}
              style={{ originX: "250px", originY: "250px" }}
            >
              <circle
                cx="250"
                cy="250"
                r="170"
                fill="none"
                stroke="#00f0ff"
                strokeWidth="20"
                strokeDasharray="70 25 110 30 40 40"
                opacity="0.5"
                filter="url(#hud-glow)"
              />
              <circle
                cx="250"
                cy="250"
                r="150"
                fill="none"
                stroke="#38bdf8"
                strokeWidth="1.5"
                strokeDasharray="8 6"
                opacity="0.7"
              />
            </motion.g>

            {/* Ring 3 - High-Speed Core Gear Teeth */}
            <motion.g
              animate={{ rotate: 360 }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              style={{ originX: "250px", originY: "250px" }}
            >
              <circle
                cx="250"
                cy="250"
                r="125"
                fill="none"
                stroke="#00f0ff"
                strokeWidth="14"
                strokeDasharray="6 8"
                opacity="0.6"
                filter="url(#hud-glow)"
              />
            </motion.g>

            {/* Ring 4 - Deep Core Concentric Cyan Energy Rings */}
            <motion.circle
              cx="250"
              cy="250"
              r="95"
              fill="none"
              stroke="#00f0ff"
              strokeWidth="4"
              strokeDasharray="40 15 90 20"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              filter="url(#hud-glow)"
            />

            {/* Central Glowing Core Orb */}
            <circle
              cx="250"
              cy="250"
              r="60"
              fill="#00f0ff"
              opacity="0.15"
              filter="url(#hud-glow)"
            />
            <motion.circle
              cx="250"
              cy="250"
              r="45"
              fill="#1b2d48"
              stroke="#00f0ff"
              strokeWidth="2"
              filter="url(#hud-glow)"
              animate={{ scale: [0.95, 1.05, 0.95] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              style={{ originX: "250px", originY: "250px" }}
            />
          </svg>

          {/* Central Reactor Counter & Status in the Core */}
          <div className="absolute flex flex-col items-center justify-center text-center">
            <span className="text-[10px] font-mono tracking-widest text-[#00f0ff]/70 uppercase">
              {progress < 100 ? 'System Boot' : 'Access Granted'}
            </span>
            <div className="text-2xl sm:text-3xl font-mono font-bold text-white drop-shadow-[0_0_12px_#00f0ff]">
              {progress}%
            </div>
            <span className="text-[9px] font-mono text-[#38bdf8]/60">
              {progress < 100 ? 'INITIALIZING...' : 'OPENING GATE'}
            </span>
          </div>

          {/* HUD Tech Readout Panels Surrounding the Reactor */}
          <div className="absolute -bottom-16 sm:-bottom-20 flex flex-col items-center gap-2">
            <div className="w-48 sm:w-64 h-[3px] bg-slate-800/80 rounded-full overflow-hidden border border-[#00f0ff]/30 shadow-[0_0_10px_rgba(0,240,255,0.2)]">
              <motion.div
                className="h-full bg-gradient-to-r from-[#0284c7] via-[#00f0ff] to-[#67e8f9] shadow-[0_0_10px_#00f0ff]"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex items-center gap-4 text-[10px] font-mono text-slate-400">
              <span>CORE: <strong className="text-[#00f0ff]">ONLINE</strong></span>
              <span>DATA: <strong className="text-[#38bdf8]">SYNCHRONIZED</strong></span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
